import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, trackPageview } from "@/lib/analytics";
import { captureAttribution, sendLanding } from "@/lib/attribution";
import { api } from "@/lib/api";
import Masthead from "@/components/Masthead";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Category from "@/pages/Category";
import Article from "@/pages/Article";
import About from "@/pages/About";
import Assessment from "@/pages/Assessment";
import AssessmentResult from "@/pages/AssessmentResult";
import Scout from "@/pages/Scout";
import PracticePlan from "@/pages/PracticePlan";
import ParentScript from "@/pages/ParentScript";
import Products from "@/pages/Products";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";
import Shop from "@/pages/Shop";
import LockerRoom from "@/pages/LockerRoom";
import Nominate from "@/pages/Nominate";
import Ring from "@/pages/Ring";
import Series from "@/pages/Series";
import Chalkos from "@/pages/Chalkos";
import Rulebook from "@/pages/Rulebook";
import Search from "@/pages/Search";
import QuickPlays from "@/pages/QuickPlays";
import QuickPlay from "@/pages/QuickPlay";
import StarterKitFeedback from "@/pages/StarterKitFeedback";
import StarterKit from "@/pages/StarterKit";
import ProductPage from "@/pages/ProductPage";
import SnapCard from "@/pages/SnapCard";
import SpeechVault from "@/pages/SpeechVault";
import CoachReady from "@/pages/CoachReady";
import CoachReadyLearn from "@/pages/CoachReadyLearn";
import CoachReadyLesson from "@/pages/CoachReadyLesson";
import CoachReadyVerify from "@/pages/CoachReadyVerify";
import CoachReadySeason from "@/pages/CoachReadySeason";
import Rookie from "@/pages/Rookie";
import CheatSheet from "@/pages/CheatSheet";
import TeachMeHowToFootball from "@/pages/TeachMeHowToFootball";
import CoachReadyUnsubscribe from "@/pages/CoachReadyUnsubscribe";
import Account from "@/pages/Account";
import AccountVerify from "@/pages/AccountVerify";
import StarterKitOrder from "@/pages/StarterKitOrder";
import QuickPlayResult from "@/pages/QuickPlayResult";
import Consent from "@/pages/Consent";
import Legal from "@/pages/Legal";
import Disclaimer from "@/pages/Disclaimer";
import ScoutChat from "@/components/ScoutChat";
import NewsletterPopup from "@/components/NewsletterPopup";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => { captureAttribution(); initAnalytics(); sendLanding(api); }, []);
  useEffect(() => {
    if (location.search.includes("utm_source=")) captureAttribution();
    trackPageview(location.pathname.replace(/^\/coach-ready\/learn\/[^/]+/, "/coach-ready/learn/:token") + location.search);
  }, [location]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F3F1EC]">
      <BrowserRouter>
        <RouteTracker />
        <Masthead />
        <main className="flex-1 pb-24">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/category/:slug" element={<Category />} />
            <Route path="/article/:slug" element={<Article />} />
            <Route path="/about" element={<About />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/assessment/result/:accountId" element={<AssessmentResult />} />
            <Route path="/scout" element={<Scout />} />
            <Route path="/tools/practice-plan" element={<PracticePlan />} />
            <Route path="/tools/parent-script" element={<ParentScript />} />
            <Route path="/products" element={<Products />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:id" element={<ProductPage />} />
            <Route path="/snap-card" element={<SnapCard />} />
            <Route path="/rookie" element={<Rookie />} />
            <Route path="/cheat-sheet" element={<CheatSheet />} />
            <Route path="/teach-me-how-to-football" element={<TeachMeHowToFootball />} />
            <Route path="/speech-vault" element={<SpeechVault />} />
            <Route path="/coach-ready" element={<CoachReady />} />
            <Route path="/coach-ready/learn" element={<CoachReadyLearn />} />
            <Route path="/coach-ready/learn/:token" element={<CoachReadyLearn />} />
            <Route path="/coach-ready/learn/:token/:n" element={<CoachReadyLesson />} />
            <Route path="/coach-ready/verify/:id" element={<CoachReadyVerify />} />
            <Route path="/coach-ready/season" element={<CoachReadySeason />} />
            <Route path="/coach-ready/unsubscribe" element={<CoachReadyUnsubscribe />} />
            <Route path="/locker-room" element={<LockerRoom />} />
            <Route path="/nominate" element={<Nominate />} />
            <Route path="/ring" element={<Ring />} />
            <Route path="/series/:name" element={<Series />} />
            <Route path="/chalkos" element={<Chalkos />} />
            <Route path="/rulebook" element={<Rulebook />} />
            <Route path="/search" element={<Search />} />
            <Route path="/quick-plays" element={<QuickPlays />} />
            <Route path="/quick-plays/r/:token" element={<QuickPlayResult />} />
            <Route path="/quick-plays/:slug" element={<QuickPlay />} />
            <Route path="/consent/:token" element={<Consent />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/verify" element={<AccountVerify />} />
            <Route path="/starter-kit" element={<StarterKit />} />
            <Route path="/starter-kit/order/:token" element={<StarterKitOrder />} />
            <Route path="/starter-kit/feedback/:token" element={<StarterKitFeedback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <ScoutChat />
        <NewsletterPopup />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
